
import { ApplicationUserModel, MaterialModel, UnitOfMeasureModel } from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import Page from '@/Page.vue';
import { Component, Ref } from 'vue-property-decorator';
import { applicationUserStore } from '@/libs/user-settings/+state/store';
import { Guid } from '@/libs/common/functions/guid';
import { scrapCausesStore as materialsStore } from '@/libs/material/+state/store';
import { scrapCausesStore as unitOfMeasuresStore } from '@/libs/unit-of-measure/+state/store';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';

@Component({})
export default class MaterialDialog extends Page {
    id!: string;
    dialog = false;
    valid = true;
    saving = false;
    loading = false;
    applicationUser: ApplicationUserModel = applicationUserStore.useGetter(CrudGetter.Detail) as ApplicationUserModel;
    material: MaterialModel | null = null;
    deleteDialog = false;
    deleting = false;

    @Ref() form!: any;

    get isNewItem(): boolean {
        return this.material?.id == Guid.EmptyGuid();
    }

    open(id: string) {
        this.dialog = true;
        this.id = id;
        this.load();
    }

    close() {
        this.dialog = false;
    }

    private load(): Promise<any> {
        if (this.id != Guid.EmptyGuid()) {
            this.loading = true;
            return new Promise((resolve, reject) => {
                this.loadUnitOfMeasures()
                    .then(() => this.loadMaterial())
                    .then((e) => {
                        this.loading = false;
                        resolve(e);
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            });
        } else {
            this.material = {
                id: Guid.EmptyGuid(),
                code: '',
                name: '',
                costs: null,
                unitOfMeasureId: null,
            } as MaterialModel;
            return this.loadUnitOfMeasures();
        }
    }

    private loadMaterial() {
        return new Promise((resolve, reject) => {
            materialsStore.dispatch(CrudAction.Get, { id: this.id });
            this.subscribe(materialsStore, CrudReponse.Get)
                .then((e) => {
                    this.material = e;
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    private loadUnitOfMeasures() {
        return new Promise((resolve, reject) => {
            unitOfMeasuresStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload<UnitOfMeasureModel>([{ field: 'name', index: 1, order: 'asc' }])
            );
            this.subscribe(unitOfMeasuresStore, CrudReponse.GetAll)
                .then(() => resolve(null))
                .catch((e: any) => reject(e));
        });
    }

    get unitOfMeasures() {
        const items = (unitOfMeasuresStore.useGetter(CrudGetter.Data) as any[]) ?? [];
        return items
            .map((x) => ({ id: x.id, label: `${x.shortCode ?? x.code ?? x.name ?? ''}`.trim() }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    private save() {
        if (this.form.validate()) {
            const material = { ...this.material } as MaterialModel;
            this.saving = true;
            if (this.id == Guid.EmptyGuid()) {
                materialsStore.dispatch(CrudAction.Create, { item: material });
                this.subscribe(materialsStore, CrudReponse.Create)
                    .then(() => {
                        this.load().then(() => {
                            this.saving = false;
                            this.close();
                        });
                    })
                    .catch((e) => {
                        this.saving = false;
                        throw e;
                    });
            } else {
                materialsStore.dispatch(CrudAction.Update, { item: material });
                this.subscribe(materialsStore, CrudReponse.Update)
                    .then(() => {
                        this.load().then(() => {
                            this.saving = false;
                            this.close();
                        });
                    })
                    .catch((e) => {
                        this.saving = false;
                        throw e;
                    });
            }
        }
    }

    deleteClick() {
        this.deleteDialog = true;
    }

    deleteMaterial() {
        this.deleting = true;
        materialsStore.dispatch(CrudAction.Delete, { id: this.id });
        this.subscribe(materialsStore, CrudReponse.Delete)
            .then(() => {
                this.deleting = false;
                this.deleteDialog = false;
                this.close();
            })
            .catch(() => {
                this.deleting = false;
            });
    }
}
